import { useState, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  CalendarDays, FileText, CheckCircle2, XCircle, Clock,
  Printer, ChevronDown, ChevronRight, Ban,
} from "lucide-react";
import { FileDropZone } from "@/components/ui/FileDropZone";
import { DatePicker } from "@/components/ui/DatePicker";
import { countWorkingDays, buildCompanyHolidaySet } from "@/utils/workingDays";
import { Button } from "@/components/ui/Button";
import { Badge } from "@/components/ui/Badge";
import { Modal } from "@/components/ui/Modal";
import { PageLoader } from "@/components/ui/Spinner";
import { useAuthStore } from "@/store/auth";
import {
  useLeaveRequests,
  useCreateLeaveRequest,
  useApproveLeave,
  useRejectLeave,
  useWithdrawLeave,
  useRequestCancellationLeave,
  useApproveCancellationLeave,
} from "../hooks/useLeaves";
import { saveBlobResponse } from "@/utils/download";
import { formatDate } from "@/utils/formatDate";
import { leavesApi } from "../api/leaves";
import {
  LEAVE_TYPE_LABELS,
  LEAVE_STATUS_COLORS,
  fmtHourlyDuration,
  fmtPartTimeDuration,
  type LeaveType,
  type LeaveRequest,
} from "../types";

const LEAVE_TYPES: { value: LeaveType; label: string }[] = [
  { value: "annual", label: "Urlop wypoczynkowy" },
  { value: "circumstantial", label: "Urlop okolicznościowy" },
  { value: "rehabilitation", label: "Urlop rehabilitacyjny" },
  { value: "care", label: "Urlop opiekuńczy" },
  { value: "training", label: "Urlop szkoleniowy" },
  { value: "other", label: "Urlop inny" },
];

const HOURS_OPTIONS = [0, 1, 2, 3, 4, 5, 6, 7];
const MINUTES_OPTIONS = [0, 15, 30, 45];

function fmtDays(req: LeaveRequest): string {
  if (req.is_hourly) return fmtHourlyDuration(req.hours_count, req.minutes_count);
  const days = req.days_count;
  const base = `${days} ${days === 1 ? "dzień roboczy" : "dni robocze"}`;
  const pt = fmtPartTimeDuration(days, req.employee_fraction_numerator, req.employee_fraction_denominator);
  return pt ? `${base}, ${pt}` : base;
}

function leaveTypeLabel(req: LeaveRequest): string {
  let base = req.leave_type === "other" && req.other_type
    ? `Urlop inny — ${req.other_type}`
    : LEAVE_TYPE_LABELS[req.leave_type] ?? req.leave_type_display;
  if (req.is_hourly) base += ` (${fmtHourlyDuration(req.hours_count, req.minutes_count)})`;
  return base;
}

const CAN_REVIEW_ROLES = ["hr", "accountant", "director"];

function StatusIcon({ status }: { status: string }) {
  if (status === "approved") return <CheckCircle2 size={14} className="text-green-600" />;
  if (status === "rejected") return <XCircle size={14} className="text-red-500" />;
  return <Clock size={14} className="text-yellow-500" />;
}

// ── "Moje urlopy" ─────────────────────────────────────────────────────────────

function MyLeaveRequests({ onNew }: { onNew: () => void }) {
  const { user } = useAuthStore();
  const canReview = CAN_REVIEW_ROLES.includes(user?.role ?? "");

  const { data: allRequests = [], isLoading } = useLeaveRequests();
  const requests = canReview
    ? allRequests.filter((r) => r.employee === user?.id)
    : allRequests;

  const withdrawLeave = useWithdrawLeave();
  const [withdrawModal, setWithdrawModal] = useState<LeaveRequest | null>(null);
  const [withdrawError, setWithdrawError] = useState("");

  const requestCancellation = useRequestCancellationLeave();
  const [cancelModal, setCancelModal] = useState<LeaveRequest | null>(null);
  const [cancelComment, setCancelComment] = useState("");
  const [cancelError, setCancelError] = useState("");

  const submitWithdraw = () => {
    if (!withdrawModal) return;
    withdrawLeave.mutate(withdrawModal.id, {
      onSuccess: () => { setWithdrawModal(null); setWithdrawError(""); },
      onError: () => setWithdrawError("Błąd podczas wycofywania wniosku."),
    });
  };

  const submitCancellation = () => {
    if (!cancelModal) return;
    requestCancellation.mutate(
      { id: cancelModal.id, comment: cancelComment },
      {
        onSuccess: () => { setCancelModal(null); setCancelComment(""); setCancelError(""); },
        onError: () => setCancelError("Błąd podczas składania prośby o anulowanie."),
      }
    );
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <CalendarDays size={16} className="text-gray-400 dark:text-gray-500" />
          <h2 className="text-base font-semibold text-gray-900 dark:text-gray-100">Moje urlopy</h2>
        </div>
        <Button onClick={onNew}>
          <CalendarDays size={15} className="mr-1.5" /> Nowy wniosek
        </Button>
      </div>

      {isLoading ? (
        <PageLoader />
      ) : requests.length === 0 ? (
        <div className="bg-white dark:bg-gray-800 p-8 text-center">
          <CalendarDays size={32} className="text-gray-300 dark:text-gray-600 mx-auto mb-2" />
          <p className="text-gray-400 dark:text-gray-500 text-sm">Brak złożonych wniosków urlopowych.</p>
        </div>
      ) : (
        <div className="flex flex-col gap-3">
          {requests.map((req) => (
            <div key={req.id} className="bg-white dark:bg-gray-800 px-4 py-4">
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-semibold text-gray-900 dark:text-gray-100 text-sm">
                      {leaveTypeLabel(req)}
                    </span>
                    <Badge color={LEAVE_STATUS_COLORS[req.status]}>
                      <StatusIcon status={req.status} />
                      <span className="ml-1">{req.status_display}</span>
                    </Badge>
                  </div>
                  <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                    {req.is_hourly ? formatDate(req.start_date) : `${formatDate(req.start_date)} – ${formatDate(req.end_date)}`}
                    <span className="ml-2 text-gray-400 dark:text-gray-500">({fmtDays(req)})</span>
                  </p>
                  {req.note && (
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 italic">{req.note}</p>
                  )}
                  {req.review_comment && (
                    <p className={`text-xs mt-1 font-medium ${req.status === "rejected" ? "text-red-600" : "text-green-700"}`}>
                      Komentarz: {req.review_comment}
                    </p>
                  )}
                  {req.cancellation_comment && req.status === "cancellation_pending" && (
                    <p className="text-xs mt-1 text-yellow-700 dark:text-yellow-400 italic">
                      Powód anulowania: {req.cancellation_comment}
                    </p>
                  )}
                </div>
                <div className="flex flex-col items-end gap-2 flex-shrink-0">
                  {req.attachment_managed_file_detail?.url && (
                    <a
                      href={req.attachment_managed_file_detail.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-1 text-xs text-[var(--color-primary)] hover:underline"
                    >
                      <FileText size={12} /> Skan
                    </a>
                  )}
                  <span className="text-xs text-gray-400 dark:text-gray-500">
                    {new Date(req.created_at).toLocaleDateString("pl-PL")}
                  </span>
                  {req.status === "pending" && (
                    <button
                      onClick={() => { setWithdrawError(""); setWithdrawModal(req); }}
                      className="flex items-center gap-1 text-xs text-orange-600 dark:text-orange-400 hover:underline mt-1"
                    >
                      <XCircle size={11} /> Wycofaj wniosek
                    </button>
                  )}
                  {req.status === "approved" && (
                    <button
                      onClick={() => { setCancelComment(""); setCancelError(""); setCancelModal(req); }}
                      className="flex items-center gap-1 text-xs text-red-600 dark:text-red-400 hover:underline mt-1"
                    >
                      <Ban size={11} /> Poproś o anulowanie
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <Modal open={!!withdrawModal} onClose={() => setWithdrawModal(null)} title="Wycofaj wniosek urlopowy">
        <div className="flex flex-col gap-4">
          {withdrawModal && (
            <div className="bg-orange-50 dark:bg-orange-900/20 border border-orange-200 dark:border-orange-800 rounded-lg p-3 text-sm">
              <p className="font-medium text-orange-800 dark:text-orange-300">{leaveTypeLabel(withdrawModal)}</p>
              <p className="text-orange-700 dark:text-orange-400 text-xs mt-0.5">
                {withdrawModal.is_hourly ? formatDate(withdrawModal.start_date) : `${formatDate(withdrawModal.start_date)} – ${formatDate(withdrawModal.end_date)}`}
                <span className="ml-1">({fmtDays(withdrawModal)})</span>
              </p>
            </div>
          )}
          <p className="text-sm text-gray-600 dark:text-gray-400">
            Czy na pewno chcesz wycofać ten wniosek urlopowy? Operacja jest nieodwracalna.
          </p>
          {withdrawError && <p className="text-sm text-red-600">{withdrawError}</p>}
          <div className="flex gap-3">
            <Button loading={withdrawLeave.isPending} onClick={submitWithdraw} variant="secondary">
              <XCircle size={14} className="mr-1.5" /> Wycofaj wniosek
            </Button>
            <Button variant="secondary" onClick={() => setWithdrawModal(null)}>Zamknij</Button>
          </div>
        </div>
      </Modal>

      <Modal open={!!cancelModal} onClose={() => setCancelModal(null)} title="Prośba o anulowanie urlopu">
        <div className="flex flex-col gap-4">
          {cancelModal && (
            <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-3 text-sm">
              <p className="font-medium text-yellow-800 dark:text-yellow-300">{leaveTypeLabel(cancelModal)}</p>
              <p className="text-yellow-700 dark:text-yellow-400 text-xs mt-0.5">
                {cancelModal.is_hourly ? formatDate(cancelModal.start_date) : `${formatDate(cancelModal.start_date)} – ${formatDate(cancelModal.end_date)}`}
                <span className="ml-1">({fmtDays(cancelModal)})</span>
              </p>
            </div>
          )}
          <div>
            <label className="text-sm font-medium text-gray-700 dark:text-gray-300 block mb-1">
              Komentarz <span className="text-gray-400 text-xs">(opcjonalny)</span>
            </label>
            <textarea
              rows={2}
              className="block w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-2 text-sm"
              value={cancelComment}
              onChange={(e) => setCancelComment(e.target.value)}
              placeholder="Powód prośby o anulowanie..."
            />
          </div>
          {cancelError && <p className="text-sm text-red-600">{cancelError}</p>}
          <div className="flex gap-3">
            <Button loading={requestCancellation.isPending} onClick={submitCancellation} variant="secondary">
              <Ban size={14} className="mr-1.5" /> Wyślij prośbę
            </Button>
            <Button variant="secondary" onClick={() => setCancelModal(null)}>Anuluj</Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}

// ── "Wnioski urlopowe do zatwierdzenia" ───────────────────────────────────────

function LeaveReviewSection() {
  const user = useAuthStore((s) => s.user);
  const { data: requests = [], isLoading } = useLeaveRequests({ status: "pending,cancellation_pending" });
  const approveLeave = useApproveLeave();
  const rejectLeave = useRejectLeave();
  const approveCancellation = useApproveCancellationLeave();

  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [approveModal, setApproveModal] = useState<LeaveRequest | null>(null);
  const [approveComment, setApproveComment] = useState("");
  const [approveError, setApproveError] = useState("");
  const [rejectModal, setRejectModal] = useState<LeaveRequest | null>(null);
  const [rejectComment, setRejectComment] = useState("");
  const [rejectError, setRejectError] = useState("");
  const [cancellationModal, setCancellationModal] = useState<LeaveRequest | null>(null);
  const [cancellationError, setCancellationError] = useState("");

  const toggle = (id: string) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });

  const submitApprove = () => {
    if (!approveModal) return;
    approveLeave.mutate(
      { id: approveModal.id, comment: approveComment },
      {
        onSuccess: () => { setApproveModal(null); setApproveComment(""); setApproveError(""); },
        onError: (err: unknown) => {
          const detail = (err as { response?: { data?: { errors?: { detail?: string } } } })
            ?.response?.data?.errors?.detail;
          setApproveError(detail ?? "Błąd podczas zatwierdzania.");
        },
      }
    );
  };

  const submitApproveCancellation = () => {
    if (!cancellationModal) return;
    approveCancellation.mutate(cancellationModal.id, {
      onSuccess: () => { setCancellationModal(null); setCancellationError(""); },
      onError: () => setCancellationError("Błąd podczas anulowania urlopu."),
    });
  };

  const submitReject = () => {
    if (!rejectModal) return;
    if (!rejectComment.trim()) { setRejectError("Komentarz jest wymagany."); return; }
    rejectLeave.mutate(
      { id: rejectModal.id, comment: rejectComment },
      {
        onSuccess: () => { setRejectModal(null); setRejectComment(""); setRejectError(""); },
        onError: () => setRejectError("Błąd podczas odrzucania."),
      }
    );
  };

  return (
    <div>
      <div className="flex items-center gap-2 mb-1">
        <CalendarDays size={16} className="text-gray-400 dark:text-gray-500" />
        <h2 className="text-base font-semibold text-gray-900 dark:text-gray-100">
          Wnioski urlopowe do zatwierdzenia
        </h2>
        {requests.length > 0 && (
          <span className="bg-yellow-100 dark:bg-yellow-900/40 text-yellow-800 dark:text-yellow-300 text-xs font-semibold px-2 py-0.5 rounded-full">
            {requests.length}
          </span>
        )}
      </div>
      <p className="text-xs text-[var(--color-primary)] font-medium mb-3">
        {user?.is_ceo || user?.role === "hr" || user?.role === "accountant"
          ? "Wszystkie oddziały"
          : user?.branch?.name
          ? `Oddział: ${user.branch.name}`
          : null}
      </p>
      {requests.some((r) => r.status === "cancellation_pending") && (
        <p className="text-xs text-yellow-700 dark:text-yellow-400 mb-3 flex items-center gap-1">
          <Ban size={11} /> Zawiera prośby o anulowanie zatwierdzonych urlopów
        </p>
      )}

      {isLoading ? (
        <PageLoader />
      ) : requests.length === 0 ? (
        <div className="bg-white dark:bg-gray-800 p-8 text-center">
          <CheckCircle2 size={32} className="text-gray-300 dark:text-gray-600 mx-auto mb-2" />
          <p className="text-gray-400 dark:text-gray-500 text-sm">Brak oczekujących wniosków.</p>
        </div>
      ) : (
        <div className="flex flex-col gap-3">
          {requests.map((req) => {
            const isOpen = expanded.has(req.id);
            return (
              <div key={req.id} className="bg-white dark:bg-gray-800 overflow-hidden">
                <div className="flex items-center gap-3 px-4 py-3">
                  <button
                    onClick={() => toggle(req.id)}
                    className="text-gray-400 hover:text-gray-600 flex-shrink-0"
                  >
                    {isOpen ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                  </button>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-semibold text-sm text-gray-900 dark:text-gray-100">
                        {req.employee_name}
                      </span>
                      <span className="text-xs text-gray-500 dark:text-gray-400">
                        {leaveTypeLabel(req)}
                      </span>
                    </div>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                      {req.is_hourly ? formatDate(req.start_date) : `${formatDate(req.start_date)} – ${formatDate(req.end_date)}`}
                      <span className="ml-2 text-gray-400">({fmtDays(req)})</span>
                    </p>
                  </div>
                  {req.status === "pending" && (
                    <div className="flex gap-2 flex-shrink-0">
                      <button
                        onClick={() => { setApproveComment(""); setApproveError(""); setApproveModal(req); }}
                        className="flex items-center gap-1 text-xs bg-green-600 text-white px-2.5 py-1.5 rounded-md hover:bg-green-700 font-medium"
                      >
                        <CheckCircle2 size={12} /> Zatwierdź
                      </button>
                      <button
                        onClick={() => { setRejectComment(""); setRejectError(""); setRejectModal(req); }}
                        className="flex items-center gap-1 text-xs bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 px-2.5 py-1.5 rounded-md hover:bg-gray-50 font-medium"
                      >
                        <XCircle size={12} /> Odrzuć
                      </button>
                    </div>
                  )}
                  {req.status === "cancellation_pending" && (
                    <button
                      onClick={() => { setCancellationError(""); setCancellationModal(req); }}
                      className="flex items-center gap-1 text-xs bg-red-600 text-white px-2.5 py-1.5 rounded-md hover:bg-red-700 font-medium flex-shrink-0"
                    >
                      <Ban size={12} /> Anuluj urlop
                    </button>
                  )}
                </div>
                {isOpen && (
                  <div className="border-t border-gray-100 dark:border-gray-700 px-4 py-3 bg-gray-50 dark:bg-gray-800/50 text-sm flex flex-col gap-2">
                    {req.status === "cancellation_pending" && req.cancellation_comment && (
                      <p className="text-yellow-700 dark:text-yellow-400">
                        <span className="font-medium">Powód anulowania: </span>
                        {req.cancellation_comment}
                      </p>
                    )}
                    {req.note && (
                      <p className="text-gray-600 dark:text-gray-400">
                        <span className="font-medium text-gray-700 dark:text-gray-300">Uwagi: </span>
                        {req.note}
                      </p>
                    )}
                    {req.attachment_managed_file_detail?.url && (
                      <a
                        href={req.attachment_managed_file_detail.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-1.5 text-[var(--color-primary)] hover:underline w-fit"
                      >
                        <FileText size={14} /> Pobierz skan wniosku
                      </a>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      <Modal open={!!approveModal} onClose={() => setApproveModal(null)} title="Zatwierdź wniosek urlopowy">
        <div className="flex flex-col gap-4">
          {approveModal && (
            <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-3 text-sm">
              <p className="font-medium text-green-800 dark:text-green-300">{approveModal.employee_name}</p>
              <p className="text-green-700 dark:text-green-400 text-xs mt-0.5">
                {LEAVE_TYPE_LABELS[approveModal.leave_type]} ·{" "}
                {approveModal.is_hourly
                  ? `${formatDate(approveModal.start_date)} (${fmtHourlyDuration(approveModal.hours_count, approveModal.minutes_count)})`
                  : `${formatDate(approveModal.start_date)} – ${formatDate(approveModal.end_date)} (${fmtDays(approveModal)})`}
              </p>
            </div>
          )}
          <div>
            <label className="text-sm font-medium text-gray-700 dark:text-gray-300 block mb-1">
              Komentarz <span className="text-gray-400 text-xs">(opcjonalny)</span>
            </label>
            <textarea
              rows={2}
              className="block w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-2 text-sm"
              value={approveComment}
              onChange={(e) => setApproveComment(e.target.value)}
              placeholder="Dodatkowe uwagi..."
            />
          </div>
          {approveError && <p className="text-sm text-red-600">{approveError}</p>}
          <div className="flex gap-3">
            <Button loading={approveLeave.isPending} onClick={submitApprove}>
              <CheckCircle2 size={14} className="mr-1.5" /> Zatwierdź
            </Button>
            <Button variant="secondary" onClick={() => setApproveModal(null)}>Anuluj</Button>
          </div>
        </div>
      </Modal>

      <Modal open={!!rejectModal} onClose={() => setRejectModal(null)} title="Odrzuć wniosek urlopowy">
        <div className="flex flex-col gap-4">
          {rejectModal && (
            <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-3 text-sm">
              <p className="font-medium text-red-800 dark:text-red-300">{rejectModal.employee_name}</p>
              <p className="text-red-700 dark:text-red-400 text-xs mt-0.5">
                {LEAVE_TYPE_LABELS[rejectModal.leave_type]} · {formatDate(rejectModal.start_date)} – {formatDate(rejectModal.end_date)}
              </p>
            </div>
          )}
          <div>
            <label className="text-sm font-medium text-gray-700 dark:text-gray-300 block mb-1">
              Powód odrzucenia <span className="text-red-500">*</span>
            </label>
            <textarea
              rows={3}
              className="block w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-2 text-sm"
              value={rejectComment}
              onChange={(e) => setRejectComment(e.target.value)}
              placeholder="Uzasadnienie odrzucenia wniosku..."
            />
            {rejectError && <p className="text-xs text-red-500 mt-1">{rejectError}</p>}
          </div>
          <div className="flex gap-3">
            <Button loading={rejectLeave.isPending} onClick={submitReject} variant="secondary">
              <XCircle size={14} className="mr-1.5" /> Odrzuć wniosek
            </Button>
            <Button variant="secondary" onClick={() => setRejectModal(null)}>Anuluj</Button>
          </div>
        </div>
      </Modal>

      <Modal open={!!cancellationModal} onClose={() => setCancellationModal(null)} title="Zatwierdź anulowanie urlopu">
        <div className="flex flex-col gap-4">
          {cancellationModal && (
            <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-3 text-sm">
              <p className="font-medium text-red-800 dark:text-red-300">{cancellationModal.employee_name}</p>
              <p className="text-red-700 dark:text-red-400 text-xs mt-0.5">
                {LEAVE_TYPE_LABELS[cancellationModal.leave_type]} · {formatDate(cancellationModal.start_date)} – {formatDate(cancellationModal.end_date)}
              </p>
              {cancellationModal.cancellation_comment && (
                <p className="text-yellow-700 dark:text-yellow-400 text-xs mt-1 italic">
                  Powód: {cancellationModal.cancellation_comment}
                </p>
              )}
            </div>
          )}
          <p className="text-sm text-gray-600 dark:text-gray-400">
            Po zatwierdzeniu urlop zostanie anulowany, a dni urlopowe wrócą do puli pracownika.
          </p>
          {cancellationError && <p className="text-sm text-red-600">{cancellationError}</p>}
          <div className="flex gap-3">
            <Button loading={approveCancellation.isPending} onClick={submitApproveCancellation} variant="secondary">
              <Ban size={14} className="mr-1.5" /> Zatwierdź anulowanie
            </Button>
            <Button variant="secondary" onClick={() => setCancellationModal(null)}>Zamknij</Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────

export function LeavesPage() {
  const { user } = useAuthStore();
  const canReview = CAN_REVIEW_ROLES.includes(user?.role ?? "");
  const createLeave = useCreateLeaveRequest();

  const [openLeave, setOpenLeave] = useState(false);
  const [leaveType, setLeaveType] = useState<LeaveType>("annual");
  const [isHourly, setIsHourly] = useState(false);
  const [otherType, setOtherType] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [hoursCount, setHoursCount] = useState(2);
  const [minutesCount, setMinutesCount] = useState(0);
  const [note, setNote] = useState("");
  const [file, setFile] = useState<File | null>(null);
  const [leaveErrors, setLeaveErrors] = useState<Record<string, string>>({});

  const entitlementYear = startDate ? parseInt(startDate.slice(0, 4)) : new Date().getFullYear();
  const showEntitlement = leaveType === "annual";

  const { data: companyHolidays = [] } = useQuery({
    queryKey: ["company-holidays"],
    queryFn: () => leavesApi.getCompanyHolidays(),
    staleTime: 1000 * 60 * 60,
  });
  const companyHolidayDates = useMemo(
    () => buildCompanyHolidaySet(companyHolidays),
    [companyHolidays],
  );
  const { data: entitlement } = useQuery({
    queryKey: ["leave-entitlement", entitlementYear],
    queryFn: () => leavesApi.getEntitlement(entitlementYear),
    enabled: openLeave && showEntitlement,
    staleTime: 1000 * 60 * 5,
  });

  const workingDaysPreview = useMemo(
    () => (startDate && endDate && endDate >= startDate
      ? countWorkingDays(startDate, endDate, companyHolidayDates)
      : null),
    [startDate, endDate, companyHolidayDates],
  );

  const resetLeave = () => {
    setLeaveType("annual");
    setIsHourly(false);
    setOtherType("");
    setStartDate("");
    setEndDate("");
    setHoursCount(2);
    setMinutesCount(0);
    setNote("");
    setFile(null);
    setLeaveErrors({});
  };

  const isB2b = user?.contract_type === "b2b";

  const validateLeave = () => {
    const e: Record<string, string> = {};
    if (isHourly) {
      if (!startDate) e.startDate = "Wymagane";
      const total = hoursCount * 60 + minutesCount;
      if (total < 1 || total >= 480) e.hoursCount = "Łączny czas musi wynosić od 1 minuty do 7h 59min";
      if (!isB2b && !file) e.file = "Skan wniosku jest wymagany";
      return e;
    }
    if (!startDate) e.startDate = "Wymagane";
    if (!endDate) e.endDate = "Wymagane";
    if (startDate && endDate && endDate < startDate) e.endDate = "Data końcowa nie może być przed datą początkową";
    if (startDate && endDate && startDate.slice(0, 4) !== endDate.slice(0, 4))
      e.endDate = "Wniosek nie może obejmować dwóch różnych lat kalendarzowych";
    if (leaveType === "other" && !otherType.trim()) e.otherType = "Należy podać rodzaj urlopu";
    if (!isB2b && !file) e.file = "Skan wniosku jest wymagany";
    return e;
  };

  const submitLeave = () => {
    const e = validateLeave();
    if (Object.keys(e).length > 0) { setLeaveErrors(e); return; }

    const fd = new FormData();
    fd.append("leave_type", leaveType);
    fd.append("is_hourly", isHourly ? "true" : "false");
    if (isHourly) {
      fd.append("start_date", startDate);
      fd.append("end_date", startDate);
      fd.append("hours_count", String(hoursCount));
      fd.append("minutes_count", String(minutesCount));
    } else {
      if (leaveType === "other") fd.append("other_type", otherType.trim());
      fd.append("start_date", startDate);
      fd.append("end_date", endDate);
    }
    // Attachment is required for non-B2B users regardless of hourly/daily mode.
    if (file) fd.append("attachment", file);
    fd.append("note", note);

    createLeave.mutate(fd, {
      onSuccess: () => { setOpenLeave(false); resetLeave(); },
      onError: (err: unknown) => {
        const detail = (err as { response?: { data?: { errors?: { detail?: string } } } })
          ?.response?.data?.errors?.detail;
        setLeaveErrors({ submit: detail ?? "Błąd podczas składania wniosku. Spróbuj ponownie." });
      },
    });
  };

  return (
    <div className={canReview ? "max-w-7xl" : "max-w-3xl"}>
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Urlopy</h1>
        <div className="w-8 h-0.5 bg-[var(--color-primary)] mt-1 mb-1" />
        <p className="text-sm text-gray-500 dark:text-gray-400 mt-2">
          {canReview
            ? "Przeglądaj własne wnioski urlopowe i zatwierdzaj wnioski pracowników."
            : isB2b
              ? "Składaj wnioski urlopowe."
              : "Składaj wnioski urlopowe — wymagany skan papierowego wniosku."}
        </p>
      </div>

      {canReview ? (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          <div>
            <MyLeaveRequests onNew={() => { resetLeave(); setOpenLeave(true); }} />
          </div>
          <div>
            <LeaveReviewSection />
          </div>
        </div>
      ) : (
        <MyLeaveRequests onNew={() => { resetLeave(); setOpenLeave(true); }} />
      )}

      <Modal open={openLeave} onClose={() => setOpenLeave(false)} title="Nowy wniosek urlopowy">
        <div className="flex flex-col gap-4">
          <div>
            <label className="text-sm font-medium text-gray-700 dark:text-gray-300 block mb-1">
              Rodzaj urlopu
            </label>
            <select
              className="block w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-2 text-sm"
              value={leaveType}
              onChange={(e) => { setLeaveType(e.target.value as LeaveType); setOtherType(""); setStartDate(""); setEndDate(""); }}
            >
              {LEAVE_TYPES.map((t) => (
                <option key={t.value} value={t.value}>{t.label}</option>
              ))}
            </select>
            <label className="mt-2 flex items-center gap-2 cursor-pointer select-none w-fit">
              <input
                type="checkbox"
                className="w-4 h-4 rounded accent-[var(--color-primary)]"
                checked={isHourly}
                onChange={(e) => { setIsHourly(e.target.checked); setStartDate(""); setEndDate(""); }}
              />
              <span className="text-sm text-gray-700 dark:text-gray-300">Urlop godzinowy</span>
            </label>
            {showEntitlement && entitlement && (
              <div className={`mt-2 flex items-center gap-2 text-sm px-3 py-2 rounded-md border ${
                entitlement.available_days <= 0 && entitlement.available_hours_remainder <= 0 && entitlement.available_minutes_remainder <= 0
                  ? "bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800 text-red-700 dark:text-red-400"
                  : "bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800 text-blue-700 dark:text-blue-400"
              }`}>
                <CalendarDays size={14} className="flex-shrink-0" />
                <span>
                  Dostępne:{" "}
                  <strong>{entitlement.available_days}</strong>
                  {entitlement.available_days === 1 ? " dzień" : " dni"}
                  {entitlement.available_hours_remainder > 0 && (
                    <span> i <strong>{entitlement.available_hours_remainder}h</strong></span>
                  )}
                  {entitlement.available_minutes_remainder > 0 && (
                    <span> <strong>{entitlement.available_minutes_remainder}min</strong></span>
                  )}
                  {entitlement.available_overdue_days > 0 && (
                    <span className="text-xs ml-1 opacity-70">
                      (w tym {entitlement.available_overdue_days}{" "}
                      {entitlement.available_overdue_days === 1 ? "dzień zaległy" : "dni zaległych"} z
                      poprzedniego roku — wykorzystywane w pierwszej kolejności)
                    </span>
                  )}
                </span>
              </div>
            )}
          </div>
          {leaveType === "other" && (
            <div>
              <label className="text-sm font-medium text-gray-700 dark:text-gray-300 block mb-1">
                Jaki rodzaj urlopu? <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                className="block w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-2 text-sm"
                value={otherType}
                onChange={(e) => setOtherType(e.target.value)}
                placeholder="np. urlop bezpłatny, urlop wychowawczy..."
              />
              {leaveErrors.otherType && <p className="text-xs text-red-500 mt-1">{leaveErrors.otherType}</p>}
            </div>
          )}
          {isHourly ? (
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div>
                <label className="text-sm font-medium text-gray-700 dark:text-gray-300 block mb-1">
                  Data urlopu <span className="text-red-500">*</span>
                </label>
                <DatePicker
                  value={startDate}
                  onChange={setStartDate}
                />
                {leaveErrors.startDate && <p className="text-xs text-red-500 mt-1">{leaveErrors.startDate}</p>}
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700 dark:text-gray-300 block mb-1">
                  Godziny <span className="text-red-500">*</span>
                </label>
                <select
                  className="block w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-2 text-sm"
                  value={hoursCount}
                  onChange={(e) => setHoursCount(parseInt(e.target.value))}
                >
                  {HOURS_OPTIONS.map((h) => (
                    <option key={h} value={h}>{h}h</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700 dark:text-gray-300 block mb-1">
                  Minuty
                </label>
                <select
                  className="block w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-2 text-sm"
                  value={minutesCount}
                  onChange={(e) => setMinutesCount(parseInt(e.target.value))}
                >
                  {MINUTES_OPTIONS.map((m) => (
                    <option key={m} value={m}>{m}min</option>
                  ))}
                </select>
                {leaveErrors.hoursCount && <p className="text-xs text-red-500 mt-1">{leaveErrors.hoursCount}</p>}
              </div>
            </div>
          ) : (
            <div>
              <div className="flex items-center justify-between mb-1">
                <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  Zakres dat <span className="text-red-500">*</span>
                </label>
                {workingDaysPreview !== null && (
                  <span className="text-xs font-semibold text-[var(--color-primary)] bg-[var(--color-primary-dim)] dark:bg-[var(--color-primary-hover)] px-2 py-0.5 rounded-full">
                    {workingDaysPreview} {workingDaysPreview === 1 ? "dzień roboczy" : "dni roboczych"}
                    {(() => {
                      const num = user?.employment_fraction_numerator ?? 1;
                      const den = user?.employment_fraction_denominator ?? 1;
                      const pt = fmtPartTimeDuration(workingDaysPreview, num, den);
                      return pt ? ` (${pt})` : null;
                    })()}
                  </span>
                )}
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <DatePicker
                    value={startDate}
                    onChange={setStartDate}
                  />
                  {leaveErrors.startDate && <p className="text-xs text-red-500 mt-1">{leaveErrors.startDate}</p>}
                </div>
                <div>
                  <DatePicker
                    value={endDate}
                    onChange={setEndDate}
                    min={startDate}
                  />
                  {leaveErrors.endDate && <p className="text-xs text-red-500 mt-1">{leaveErrors.endDate}</p>}
                </div>
              </div>
            </div>
          )}
          <div>
            <label className="text-sm font-medium text-gray-700 dark:text-gray-300 block mb-1">
              Uwagi <span className="text-gray-400 text-xs">(opcjonalne)</span>
            </label>
            <textarea
              rows={2}
              className="block w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-2 text-sm"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="Dodatkowe informacje..."
            />
          </div>
          {!isB2b && (
            <div>
              <label className="text-sm font-medium text-gray-700 dark:text-gray-300 block mb-1">
                Skan wniosku <span className="text-red-500">*</span>
              </label>
              <FileDropZone
                files={file ? [file] : []}
                onFilesChange={(fs) => setFile(fs[0] ?? null)}
                accept=".pdf,.jpg,.jpeg,.png"
              />
              {leaveErrors.file && <p className="text-xs text-red-500 mt-1">{leaveErrors.file}</p>}
            </div>
          )}
          {leaveErrors.submit && <p className="text-sm text-red-600">{leaveErrors.submit}</p>}
          <div className="flex gap-3 flex-wrap">
            <Button loading={createLeave.isPending} onClick={submitLeave}>
              Złóż wniosek
            </Button>
            {!isB2b && !isHourly && (
              <Button
                variant="warning"
                disabled={!startDate || !endDate || endDate < startDate}
                onClick={async () => {
                  const resp = await leavesApi.generatePdf({
                    leave_type: leaveType,
                    other_type: leaveType === "other" ? otherType : undefined,
                    start_date: startDate,
                    end_date: endDate,
                    days_count: workingDaysPreview ?? 0,
                  });
                  saveBlobResponse(resp, `wniosek_urlopowy_${startDate}.pdf`);
                }}
              >
                <Printer size={14} className="mr-1.5" /> Generuj PDF
              </Button>
            )}
            {!isB2b && isHourly && (
              <Button
                variant="warning"
                disabled={!startDate || hoursCount * 60 + minutesCount < 1}
                onClick={async () => {
                  const resp = await leavesApi.generatePdf({
                    leave_type: leaveType,
                    other_type: leaveType === "other" ? otherType : undefined,
                    start_date: startDate,
                    end_date: startDate,
                    days_count: 0,
                    is_hourly: true,
                    hours_count: hoursCount,
                    minutes_count: minutesCount,
                  });
                  saveBlobResponse(resp, `wniosek_urlopowy_${startDate}.pdf`);
                }}
              >
                <Printer size={14} className="mr-1.5" /> Generuj PDF
              </Button>
            )}
            <Button variant="secondary" onClick={() => setOpenLeave(false)}>Anuluj</Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
